import { useRef, useState } from 'react'
import {
  Button,
  Callout,
  Dialog,
  DialogBody,
  DialogFooter,
  FormGroup,
  NumericInput,
  Switch,
} from '@blueprintjs/core'

import { importToolStep } from '../api'
import type { Store, ToolDefinition } from '../store'

/**
 * Cell → Tool → Import STEP…
 *
 * One-shot form: pick a STEP, name it, decide whether to flip 180°
 * about X (needed for STEP that was modelled hanging in -Z), optionally
 * supply mass. On submit the backend runs
 * ``amatl_core.tools.importer.import_torch_step`` and upserts the
 * result into the versioned per-cell library.
 *
 * The mount-frame gizmo and TCP cylinder picker from the PROMPT are
 * deferred to a later phase — the auto-detect is good enough on the
 * two Panasonic test torches we have today.
 */
export function ToolImportDialog({
  isOpen,
  onClose,
  store,
}: {
  isOpen: boolean
  onClose: () => void
  store: Store
}) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [vendor, setVendor] = useState('')
  const [part, setPart] = useState('')
  const [description, setDescription] = useState('')
  const [stickout, setStickout] = useState(15.0)
  const [flip, setFlip] = useState(false)
  const [mass, setMass] = useState<number | ''>('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const canSubmit = !!(vendor && part && fileRef.current?.files?.length)

  const submit = async () => {
    setErr(null)
    if (!fileRef.current?.files?.[0]) return
    setBusy(true)
    try {
      const tool: ToolDefinition = await importToolStep({
        file: fileRef.current.files[0],
        tool_id: `${vendor.toLowerCase().replace(/\W+/g, '')}_${part}`,
        vendor,
        part,
        description,
        stickout_mm: stickout,
        flip_x_180: flip,
        mass_kg: typeof mass === 'number' ? mass : null,
      })
      await store.loadToolLibrary()
      store.setActiveTool(tool.id)
      onClose()
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : String(ex))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Import torch STEP"
      icon="import"
      style={{ width: 540 }}
    >
      <DialogBody>
        <FormGroup label="STEP file" labelInfo="(.step / .stp)">
          <input
            ref={fileRef}
            type="file"
            accept=".step,.stp,.STEP,.STP"
            className="bp5-input bp5-fill"
          />
        </FormGroup>
        <FormGroup label="Vendor">
          <input
            className="bp5-input bp5-fill"
            value={vendor}
            onChange={(e) => setVendor(e.target.value)}
            placeholder="e.g. Fronius"
          />
        </FormGroup>
        <FormGroup label="Part">
          <input
            className="bp5-input bp5-fill"
            value={part}
            onChange={(e) => setPart(e.target.value)}
            placeholder="e.g. Robacta-500"
          />
        </FormGroup>
        <FormGroup label="Description">
          <input
            className="bp5-input bp5-fill"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="500 A water-cooled robot torch"
          />
        </FormGroup>
        <FormGroup label="Stickout (mm)">
          <NumericInput
            min={5}
            max={50}
            stepSize={1}
            value={stickout}
            onValueChange={(v) => setStickout(Number.isFinite(v) ? v : 15)}
          />
        </FormGroup>
        <FormGroup label="Mass (kg)" helperText="Required before RAPID export.">
          <NumericInput
            min={0}
            stepSize={0.1}
            value={mass}
            onValueChange={(v) => setMass(Number.isFinite(v) ? v : '')}
          />
        </FormGroup>
        <FormGroup
          label="Flip 180° about X"
          helperText="Enable if the STEP is modelled hanging in -Z (e.g. WSATU-type torches)."
          inline
        >
          <Switch checked={flip} onChange={(e) => setFlip((e.target as HTMLInputElement).checked)} />
        </FormGroup>
        <Callout intent="primary" icon="info-sign">
          Auto-detect: mount frame + bolt PCD + neck axis + wire exit + TCP.
          You can edit the result in the Cell / Tool panel after import.
        </Callout>
        {err && (
          <Callout intent="danger" icon="error" style={{ marginTop: 10 }}>
            {err}
          </Callout>
        )}
      </DialogBody>
      <DialogFooter
        actions={
          <>
            <Button onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              intent="primary"
              icon="import"
              onClick={submit}
              loading={busy}
              disabled={!canSubmit || busy}
            >
              Import
            </Button>
          </>
        }
      />
    </Dialog>
  )
}
